// supervisor-git-runner.ts
//
// Real `GitRunner` (commit-owner-git-ops.ts's injectable shape) backed by
// spawnAsyncCapture — the production implementation the supervisor wires
// into commit-owner.ts. Tests inject their OWN runner (argv-recording fake)
// per commit-owner.test.ts's pattern; this file exists so production code
// has exactly one place that constructs a real one instead of every call
// site rolling its own spawn wrapper.
//
// 06g R3 (audit H2, debate N5): every argv is prefixed with
// `-c core.hooksPath=/dev/null` — git treats a hooks path that is not a
// directory as "no hooks", so a repo's pre-commit/commit-msg/pre-push/… never
// execute inside the controller process (dev's full env). One place covers
// commit AND push (draft-merge-request-push.ts uses this same runner). Chosen
// over `--no-verify`, which skips only two commit hooks and nothing on push.
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";
import { GIT_TIMEOUT_SECONDS, type GitRunner } from "./commit-owner-git-ops.js";

export const GIT_HOOKS_DISABLED_ARGS = ["-c", "core.hooksPath=/dev/null"] as const;

/** `timeoutMs` injectable for tests (default = commit-owner's 30 s contract). */
export function createGitRunner(options: { timeoutMs?: number } = {}): GitRunner {
  const timeoutMs = options.timeoutMs ?? GIT_TIMEOUT_SECONDS * 1000;
  return async (args, cwd, callOptions) => {
    const res = await spawnAsyncCapture("git", [...GIT_HOOKS_DISABLED_ARGS, ...args], {
      cwd,
      timeoutMs,
      forwardSigint: false,
      ...(callOptions?.env ? { env: callOptions.env } : {}),
    });
    return { status: res.status, stdout: res.stdout, stderr: res.stderr, timedOut: res.timedOut };
  };
}

export const realGitRunner: GitRunner = createGitRunner();
